import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import ProfileCard from "./components/ProfileCard";
import { getFolderContents } from "./mvc/foldersController";

const FolderDetail = () => {
  const { folderId, type } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [folderName, setFolderName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ leadCount: 0, clientCount: 0 });

  const showToast = (message, toastType) => {
    setToast({ show: true, message, type: toastType });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const fetchFolderContents = useCallback(async () => {
    if (!folderId) {
      setError("Folder ID is missing");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await getFolderContents(folderId, currentPage, pageLimit);

      setItems(response.items);
      setTotal(response.total);
      setFolderName(response.folderName);
      setStats({
        leadCount: response.leadCount,
        clientCount: response.clientCount,
      });

      if (!response.success) {
        setError(response.message || "Failed to load folder contents");
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || "Failed to fetch folder contents";
      setError(errorMsg);
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  }, [folderId, currentPage, pageLimit]);

  useEffect(() => {
    fetchFolderContents();
  }, [fetchFolderContents]);

  const handleBack = () => {
    const normalizedType = type?.toString().toLowerCase();
    const pluralType = normalizedType === "client" || normalizedType === "clients" ? "clients" : "leads";
    navigate(`/admin/folders/${pluralType}`);
  };

  return (
    <div className="w-full mt-3 h-full px-3 md:px-6 lg:px-10">
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed right-6 top-20 z-[60] rounded-lg px-4 py-3 text-sm font-semibold shadow-md backdrop-blur-sm ${
            toast.type === "error"
              ? "border border-red-400/70 bg-red-500/20 text-red-700"
              : "border border-green-400/70 bg-green-500/20 text-green-700"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={handleBack}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-500 transition-colors duration-200"
            title="Back to Folders"
          >
            <IoArrowBack className="text-2xl font-bold" />
          </button>
          <h1 className="text-3xl font-bold text-navy-700 dark:text-white flex-shrink-0">
            {folderName || "Folder Contents"}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Manage and view all items in this folder
        </p>

        {/* Stats */}
        <div className="flex gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-brand-600 dark:text-brand-400">{total}</span>
            <span className="text-gray-600 dark:text-gray-400">Total Items</span>
          </div>
          {stats.leadCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.leadCount}</span>
              <span className="text-gray-600 dark:text-gray-400">Leads</span>
            </div>
          )}
          {stats.clientCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.clientCount}</span>
              <span className="text-gray-600 dark:text-gray-400">Clients</span>
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500 dark:border-gray-700 dark:border-t-brand-400" />
        </div>
      ) : items.length > 0 ? (
        <>
          {/* Profile Cards Grid */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
            {items.map((item) => (
              <ProfileCard key={item._id} item={item} type={type} />
            ))}
          </div>

          {/* Pagination */}
          {total > pageLimit && (
            <div className="flex items-center justify-center gap-2 py-6 mb-8">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Previous
              </button>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Page {currentPage} of {Math.ceil(total / pageLimit)}
              </div>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(total / pageLimit)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-16 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400 mb-4 text-lg">No items in this folder</p>
          <button
            onClick={handleBack}
            className="text-brand-500 hover:text-brand-600 font-semibold transition-colors"
          >
            Back to Folders →
          </button>
        </div>
      )}
    </div>
  );
};

export default FolderDetail;
